import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import {
  Button,
  DialogActions,
  DialogContent,
  type DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

import DraggablePaper from './DraggablePaper';
import StyledDialog from './StyledDialog';
import { type OverwatchModalProps } from './types';

const OverwatchModal: React.FC<OverwatchModalProps> = (props) => {
  const {
    open = true,
    title,
    subtitle,
    caption,
    children,
    handleClose,
    handleMinimize,
    primaryButton,
    primaryButtonText = 'Save',
    primaryButtonAction,
    disablePrimary = false,
    secondaryButton,
    secondaryButtonText = 'Cancel',
    secondaryButtonAction,
    disableSecondary = false,
    tertiaryButton,
    tertiaryButtonText,
    tertiaryButtonAction,
    disableTertiary = false,
    size = 'xl',
    draggable = false,
    resizable = false,
    dividers = true,
    hidePrimaryButton = false,
    hideSecondaryButton = false,
    hideTertiaryButton = false,
    disableEscape = false,
    disableBackdropClose = false,
    minimized = false,
    // fullScreen = false,
    contentSx,
    paperSx,
  } = props;

  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick' && disableBackdropClose) return;
    handleClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth={size}
      fullScreen={false} // disabled temporarily
      resizable={resizable}
      disableEscapeKeyDown={disableEscape}
      sx={minimized ? { display: 'none' } : undefined}
      slotProps={{ paper: { sx: paperSx } }}
      {...(draggable ? { PaperComponent: DraggablePaper, 'aria-labelledby': 'overwatch-modal-drag-handle' } : {})}
    >
      <DialogTitle
        id="overwatch-modal-drag-handle"
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          cursor: draggable ? 'move' : 'default',
        }}
      >
        <Grid container size={12} sx={{ mr: 'auto' }}>
          {title && (
            <Typography variant="h6" fontWeight={600}>
              {title}&nbsp;
            </Typography>
          )}
          {subtitle && <Typography variant="h6">{subtitle}</Typography>}
          {caption && (
            <Typography variant="caption" sx={{ width: '100%' }}>
              {caption}
            </Typography>
          )}
        </Grid>

        {handleMinimize && (
          <IconButton aria-label="minimize" onClick={handleMinimize} sx={{ color: (theme) => theme.palette.grey[500] }}>
            <MinimizeIcon />
          </IconButton>
        )}
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers={dividers} sx={contentSx}>
        {children}
      </DialogContent>

      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Grid container justifyContent="flex-start" size="grow">
          {tertiaryButton ||
            (!hideTertiaryButton && tertiaryButtonText && (
              <Button onClick={() => void tertiaryButtonAction?.()} disabled={disableTertiary}>
                {tertiaryButtonText}
              </Button>
            ))}
        </Grid>
        <Grid container justifyContent="flex-end" size="grow">
          {!hideSecondaryButton &&
            (secondaryButton || (
              <Button onClick={() => void secondaryButtonAction?.()} disabled={disableSecondary}>
                {secondaryButtonText}
              </Button>
            ))}
          {!hidePrimaryButton &&
            (primaryButton || (
              <Button variant="contained" onClick={() => void primaryButtonAction?.()} disabled={disablePrimary}>
                {primaryButtonText}
              </Button>
            ))}
        </Grid>
      </DialogActions>
    </StyledDialog>
  );
};

export default OverwatchModal;
